import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useBranding } from 'src/branding/BrandingContextProvider';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { isRole } from 'src/helpers/permissions';
import { USER_ROLES } from 'src/common/constants';
import { ServiceRequestFilters } from 'src/pages/ServiceRequests/CustomHooks/useServiceRequestFilters';
import {
  useServiceRequestDashboardApi,
  UseServiceRequestDashboardApiReturn,
} from 'src/services/api/ServiceRequestDashboard/serviceRequestDashboard';
import {
  useServiceRequestAPI,
  UseServiceRequestAPIReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useServiceRequestAPI';
import {
  IDashboardAgeOverview,
  IDashboardStatusOverview,
  IServiceRequest,
} from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { searchServiceRequestsGeneralUtil } from 'src/pages/ServiceRequests/helpers/filterServiceRequests';
import { CsvDateFormat } from 'src/services/client-types';
import {
  useDownloadCSVData,
  UseDownloadCSVDataReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/useDownloadCSVData';
import {
  useDownloadPDF,
  UseDownloadPDFReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadPDF/useDownloadPDF';
import {
  useServiceRequestsPageFormState,
  UseServiceRequestsPageFormStateReturn,
} from './useServiceRequestsPageFormState';

const RESOLVED_STATUSES: string[] = ['Resolved', 'Closed', 'Declined'];

interface BulkStatusPayload {
  serviceRequest: { id: string }[];
  name: string;
  userId: string;
}

export interface UseServiceRequestsPageOrchestratorReturn {
  serviceRequests: IServiceRequest[];
  visibleServiceRequests: IServiceRequest[];
  statusOverview: IDashboardStatusOverview;
  ageOverview: IDashboardAgeOverview;
  loading: boolean;

  searchString: string;
  setSearchString: (value: string) => void;

  selectedIds: string[];
  selectedServiceRequests: IServiceRequest[];
  selectedCount: number;
  allSelected: boolean;
  toggleAllSelected: () => void;
  handleSelectServiceRequests: (ids: string[]) => void;

  showResolved: boolean;
  handleToggleShowResolved: () => void;
  disableShowResolved: () => void;

  showSubscribedOnly: boolean;
  handleToggleShowSubscribedOnly: () => void;

  showColumnSelectionDialog: boolean;
  openColumnSelectionDialog: () => void;
  closeColumnSelectionDialog: () => void;

  canManageStatuses: boolean;
  showStatusManager: boolean;
  openStatusManager: () => void;
  closeStatusManager: () => void;

  showVisibilityDialogue: boolean;
  openVisibilityDialogue: () => void;
  closeVisibilityDialogue: () => void;

  showFilterOverlay: boolean;
  openFilterOverlay: () => void;
  closeFilterOverlay: () => void;
  setShowFilterOverlay: (value: boolean) => void;

  showGroupingOverlay: boolean;
  groupingPropertyId: string | null;
  handleOpenGroupingOverlay: () => void;
  closeGroupingOverlay: () => void;
  setShowGroupingOverlay: (value: boolean) => void;

  showDeclinedStatusOverlay: boolean;
  closeDeclinedStatusOverlay: () => void;
  setShowDeclinedStatusOverlay: (value: boolean) => void;

  showAssignedStatusOverlay: boolean;
  closeAssignedStatusOverlay: () => void;
  setShowAssignedStatusOverlay: (value: boolean) => void;

  showDownloadOverlay: boolean;
  openDownloadOverlay: () => void;
  closeDownloadOverlay: () => void;

  handleBulkStatusUpdate: (status: string) => Promise<void>;
  handleDeleteGroup: (groupId: string) => Promise<void>;
  handleDeleteItemFromGroup: (itemId: string) => Promise<void>;

  fetchData: () => Promise<void>;

  onDownloadCSV: (dateFormat: CsvDateFormat) => Promise<boolean>;
  onDownloadPDF: () => Promise<boolean>;
  isCSVLoading: boolean;
  isPDFLoading: boolean;
}

interface UseServiceRequestsPageOrchestratorParams {
  filters: ServiceRequestFilters;
  formattedFiltersForApi: any;
  applyFilter: (key: string, data: any) => void;
}

export const useServiceRequestsPageOrchestrator = ({
  filters,
  formattedFiltersForApi,
  applyFilter,
}: UseServiceRequestsPageOrchestratorParams): UseServiceRequestsPageOrchestratorReturn => {
    const { branding } = useBranding();
    const user: any = useSelector(selectAuthenticatedUser);
    const userId: string = user?.id ?? '';

    const canManageStatuses: boolean = isRole(
      [USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN],
      user
    );

    const formattedFilters: any = formattedFiltersForApi;

    const { Statuses }: { Statuses: string[] } = filters;

    const srDashboardApi: UseServiceRequestDashboardApiReturn =
      useServiceRequestDashboardApi();
    const serviceRequestApi: UseServiceRequestAPIReturn = useServiceRequestAPI();
    const csvDownload: UseDownloadCSVDataReturn = useDownloadCSVData();
    const pdfDownload: UseDownloadPDFReturn = useDownloadPDF();

    const serviceRequests: IServiceRequest[] = srDashboardApi.serviceRequests;

    const formState: UseServiceRequestsPageFormStateReturn =
      useServiceRequestsPageFormState({ serviceRequests });

    const fetchData = async (): Promise<void> => {
      formState.clearSelection();

      await Promise.all([
        srDashboardApi.fetchAllOverviews(formattedFilters),
        srDashboardApi.fetchDashboard(formattedFilters),
      ]);
    };

    const toggleAllSelected = (): void => {
      const next: boolean = !formState.allSelected;

      formState.setAllSelected(next);

      if (!next) {
        formState.clearSelection();
      }
    };

    const handleSelectServiceRequests = (ids: string[]): void => {
      if (formState.allSelected) {
        formState.setAllSelected(false);

        const remaining: string[] = serviceRequests.reduce(
          (acc: string[], curr: IServiceRequest) => {
            if (!ids.includes(curr.id)) {
              acc.push(curr.id);
            }

            return acc;
          },
          []
        );

        formState.setSelectedIds(remaining);
        return;
      }

      const anyAlreadySelected: boolean = formState.selectedIds.some(
        (r: string) => ids.includes(r)
      );

      if (anyAlreadySelected) {
        formState.setSelectedIds(
          formState.selectedIds.filter((r: string) => !ids.includes(r))
        );
        return;
      }

      formState.setSelectedIds(formState.selectedIds.concat(ids));
    };

    const clearShowResolvedFilter = (): void => {
      console.log('Statuses: ', Statuses);

      applyFilter(
        'Statuses',
        Statuses.filter((s: string) => !RESOLVED_STATUSES.includes(s))
      );
    };

    const handleToggleShowSubscribedOnly = (): void => {
      const next: boolean = !formState.showSubscribedOnly;
      formState.setShowSubscribedOnly(next);

      if (next && formState.showResolved) {
        formState.setShowResolved(false);
        clearShowResolvedFilter();
      }
    };

    const handleToggleShowResolved = (): void => {
      const next: boolean = !formState.showResolved;
      formState.setShowResolved(next);

      if (next) {
        if (formState.showSubscribedOnly) {
          formState.setShowSubscribedOnly(false);
        }

        applyFilter('Statuses', RESOLVED_STATUSES);
        return;
      }

      clearShowResolvedFilter();
    };

    const handleDeleteGroup = async (groupId: string): Promise<void> => {
      const confirmed: boolean = window.confirm(
        'Are you sure you want to delete this group?'
      );

      if (!confirmed) {
        return;
      }

      const ok: boolean = await serviceRequestApi.deleteServiceRequestGroupById(
        groupId,
        userId
      );

      if (ok) {
        await fetchData();
      }
    };

    const handleDeleteItemFromGroup = async (itemId: string): Promise<void> => {
      const srLabel: string =
        branding?.text?.serviceRequest?.toLowerCase() ?? 'service request';
      const confirmed: boolean = window.confirm(
        `Are you sure you want to remove this ${srLabel} from the group?`
      );

      if (!confirmed) {
        return;
      }

      const ok: boolean = await serviceRequestApi.removeServiceRequestFromGroup(
        itemId,
        userId
      );

      if (ok) {
        await fetchData();
      }
    };

    const handleBulkStatusUpdate = async (status: string): Promise<void> => {
      if (formState.selectedIds.length === 0 && !formState.allSelected) {
        return;
      }

      if (status === 'Declined') {
        formState.openDeclinedStatusOverlay();
        return;
      }

      if (status === 'Assigned') {
        formState.openAssignedStatusOverlay();
        return;
      }

      const ids: string[] = formState.allSelected
        ? searchServiceRequestsGeneralUtil(serviceRequests, formState.searchString).map(
            (s: IServiceRequest) => s.id
          )
        : formState.selectedIds;

      const payload: BulkStatusPayload = {
        serviceRequest: ids.map((id: string) => ({ id })),
        name: status,
        userId,
      };

      const ok: boolean = await serviceRequestApi.bulkUpdateStatus(payload);

      if (ok) {
        await fetchData();
      }
    };

    const handleOpenGroupingOverlay = (): void => {
      if (formState.selectedIds.length === 0) {
        toast.error('Please select at least one service request to group.', {
          autoClose: 6000,
          position: 'top-center',
        });
        return;
      }

      if (formState.allSelected) {
        toast.error('Please select service requests individually to group.', {
          autoClose: 6000,
          position: 'top-center',
        });
        return;
      }

      if (formState.selectedIds.length === 1) {
        formState.openGroupingOverlay();
        return;
      }

      const propertyId: string =
        formState.selectedServiceRequests[0]?.propertyId ?? '';
      formState.setGroupingPropertyId(propertyId);

      const hasMixedProperties: boolean = Boolean(
        formState.selectedServiceRequests.find(
          (r: IServiceRequest) => r.propertyId !== propertyId
        )
      );

      if (hasMixedProperties) {
        toast.error(
          'Please select service requests from the same property to group.',
          { autoClose: 6000, position: 'top-center' }
        );
        return;
      }

      formState.openGroupingOverlay();
    };

    const disableShowResolved = (): void => {
      formState.setShowResolved(false);
    };

    // legacy/workaround: FilterOverlay / GroupingOverlay / *StatusOverlay still
    // accept raw setShow* setter props instead of an onClose callback. Wrap
    // form-state's open/close into the legacy setter shape until those overlays
    // are migrated to a named-callback prop API.
    const setShowFilterOverlay = (value: boolean): void => {
      if (value) {
        formState.openFilterOverlay();
        return;
      }

      formState.closeFilterOverlay();
    };

    const setShowGroupingOverlay = (value: boolean): void => {
      if (value) {
        formState.openGroupingOverlay();
        return;
      }

      formState.closeGroupingOverlay();
    };

    const setShowDeclinedStatusOverlay = (value: boolean): void => {
      if (value) {
        formState.openDeclinedStatusOverlay();
        return;
      }

      formState.closeDeclinedStatusOverlay();
    };

    const setShowAssignedStatusOverlay = (value: boolean): void => {
      if (value) {
        formState.openAssignedStatusOverlay();
        return;
      }

      formState.closeAssignedStatusOverlay();
    };

    const onDownloadCSV = async (
      dateFormat: CsvDateFormat
    ): Promise<boolean> => {
      return await csvDownload.onDownloadCSV(
        formState.idsForExport,
        dateFormat
      );
    };

    const onDownloadPDF = async (): Promise<boolean> => {
      return await pdfDownload.onDownloadPDF(
        userId,
        formState.idsForExport.join('&')
      );
    };

    useEffect(() => {
      void fetchData();
    }, [JSON.stringify(formattedFilters)]);

    return {
      serviceRequests,
      visibleServiceRequests: formState.visibleServiceRequests,
      statusOverview: srDashboardApi.statusOverview,
      ageOverview: srDashboardApi.ageOverview,
      loading: srDashboardApi.loading,

      searchString: formState.searchString,
      setSearchString: formState.setSearchString,

      selectedIds: formState.selectedIds,
      selectedServiceRequests: formState.selectedServiceRequests,
      selectedCount: formState.selectedCount,
      allSelected: formState.allSelected,
      toggleAllSelected,
      handleSelectServiceRequests,

      showResolved: formState.showResolved,
      handleToggleShowResolved,
      disableShowResolved,

      showSubscribedOnly: formState.showSubscribedOnly,
      handleToggleShowSubscribedOnly,

      showColumnSelectionDialog: formState.showColumnSelectionDialog,
      openColumnSelectionDialog: formState.openColumnSelectionDialog,
      closeColumnSelectionDialog: formState.closeColumnSelectionDialog,

      canManageStatuses,
      showStatusManager: formState.showStatusManager,
      openStatusManager: formState.openStatusManager,
      closeStatusManager: formState.closeStatusManager,

      showVisibilityDialogue: formState.showVisibilityDialogue,
      openVisibilityDialogue: formState.openVisibilityDialogue,
      closeVisibilityDialogue: formState.closeVisibilityDialogue,

      showFilterOverlay: formState.showFilterOverlay,
      openFilterOverlay: formState.openFilterOverlay,
      closeFilterOverlay: formState.closeFilterOverlay,
      setShowFilterOverlay,

      showGroupingOverlay: formState.showGroupingOverlay,
      groupingPropertyId: formState.groupingPropertyId,
      handleOpenGroupingOverlay,
      closeGroupingOverlay: formState.closeGroupingOverlay,
      setShowGroupingOverlay,

      showDeclinedStatusOverlay: formState.showDeclinedStatusOverlay,
      closeDeclinedStatusOverlay: formState.closeDeclinedStatusOverlay,
      setShowDeclinedStatusOverlay,

      showAssignedStatusOverlay: formState.showAssignedStatusOverlay,
      closeAssignedStatusOverlay: formState.closeAssignedStatusOverlay,
      setShowAssignedStatusOverlay,

      showDownloadOverlay: formState.showDownloadOverlay,
      openDownloadOverlay: formState.openDownloadOverlay,
      closeDownloadOverlay: formState.closeDownloadOverlay,

      handleBulkStatusUpdate,
      handleDeleteGroup,
      handleDeleteItemFromGroup,

      fetchData,

      onDownloadCSV,
      onDownloadPDF,
      isCSVLoading: csvDownload.isLoading,
      isPDFLoading: pdfDownload.isLoading,
    };
  };
